import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRight, MapPin, Calendar, Maximize2, CheckCircle2, Award } from 'lucide-react';
import axios from 'axios';
import CurrentProjects from '../components/CurrentProjects';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RandIcon = ({ size = 14, className = '' }) => (
  <span
    aria-hidden="true"
    className={`inline-flex items-center justify-center font-heading font-medium leading-none ${className}`}
    style={{ width: size, height: size, fontSize: size * 1.1 }}
  >
    R
  </span>
);

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

function FeaturedProjectBlock({ project, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const isReversed = index % 2 === 1;
  const gallery = project.gallery && project.gallery.length > 0 ? project.gallery : [project.image];

  return (
    <section
      ref={ref}
      data-testid={`featured-project-${project.id}`}
      className={`py-20 md:py-28 ${
        index % 2 === 0 ? 'bg-background' : 'bg-surface border-y border-border'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start ${
            isReversed ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="image-hover-container aspect-[4/3] overflow-hidden mb-4">
              <img
                src={gallery[0]}
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {gallery.slice(1, 5).map((img, idx) => (
                  <div
                    key={idx}
                    className="aspect-square overflow-hidden border border-border hover:border-primary transition-colors"
                  >
                    <img
                      src={img}
                      alt={`${project.title} ${idx + 2}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            <motion.div variants={fadeUpVariants} className="flex items-center gap-3 mb-4">
              <Award size={16} className="text-primary" />
              <span className="overline">Featured Project // {String(index + 1).padStart(2, '0')}</span>
            </motion.div>
            <motion.span
              variants={fadeUpVariants}
              className="inline-block font-mono text-xs uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 mb-4 capitalize"
            >
              {project.category}
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-3xl md:text-4xl lg:text-5xl text-text-main tracking-tight"
            >
              {project.title}
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 text-text-muted font-body text-base md:text-lg leading-relaxed"
            >
              {project.description}
            </motion.p>

            {/* Stats Grid */}
            <motion.div
              variants={fadeUpVariants}
              className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6"
            >
              {project.location && (
                <div className="border-l-2 border-primary/40 pl-4">
                  <div className="flex items-center gap-2 text-text-muted">
                    <MapPin size={14} />
                    <span className="font-mono text-xs uppercase tracking-widest">Location</span>
                  </div>
                  <div className="font-heading text-text-main text-lg mt-1">{project.location}</div>
                </div>
              )}
              {project.completion && (
                <div className="border-l-2 border-primary/40 pl-4">
                  <div className="flex items-center gap-2 text-text-muted">
                    <Calendar size={14} />
                    <span className="font-mono text-xs uppercase tracking-widest">Completion</span>
                  </div>
                  <div className="font-heading text-text-main text-lg mt-1">{project.completion}</div>
                </div>
              )}
              {project.budget && (
                <div className="border-l-2 border-primary/40 pl-4">
                  <div className="flex items-center gap-2 text-text-muted">
                    <RandIcon size={14} />
                    <span className="font-mono text-xs uppercase tracking-widest">Budget</span>
                  </div>
                  <div className="font-heading text-text-main text-lg mt-1">{project.budget}</div>
                </div>
              )}
              {project.size && (
                <div className="border-l-2 border-primary/40 pl-4">
                  <div className="flex items-center gap-2 text-text-muted">
                    <Maximize2 size={14} />
                    <span className="font-mono text-xs uppercase tracking-widest">Size</span>
                  </div>
                  <div className="font-heading text-text-main text-lg mt-1">{project.size}</div>
                </div>
              )}
            </motion.div>

            {/* Highlights */}
            {project.highlights && project.highlights.length > 0 && (
              <motion.div variants={fadeUpVariants} className="mt-8">
                <span className="overline">Project Highlights</span>
                <ul className="mt-4 space-y-2">
                  {project.highlights.map((highlight) => (
                    <li
                      key={highlight}
                      className="flex items-center gap-3 text-text-muted text-sm"
                    >
                      <CheckCircle2 size={14} className="text-primary flex-shrink-0" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            <motion.div variants={fadeUpVariants} className="mt-10">
              <Link
                to={`/projects/${project.id}`}
                data-testid={`featured-cta-${project.id}`}
                className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest hover:gap-4 transition-all"
              >
                View Project Details
                <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [view, setView] = useState('featured');
  const gridRef = useRef(null);
  const expertiseRef = useRef(null);
  const gridInView = useInView(gridRef, { once: true, margin: '-100px' });
  const expertiseInView = useInView(expertiseRef, { once: true, margin: '-100px' });

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'retail', label: 'Retail' },
    { id: 'residential', label: 'Residential' },
    { id: 'bathroom', label: 'Bathrooms' },
    { id: 'kitchen', label: 'Kitchens' },
    { id: 'roof', label: 'Roofs' },
    { id: 'student', label: 'Student Housing' }
  ];

  const types = [
    { id: 'all', label: 'All Types' },
    { id: 'renovation', label: 'Renovations' },
    { id: 'turnkey', label: 'Turnkey' }
  ];

  const expertiseAreas = [
    {
      title: 'Insurance Damage Restoration',
      description: '5+ mobile teams operating daily on insurance repair projects'
    },
    {
      title: 'Waterproofing Solutions',
      description: 'Advanced waterproofing systems for commercial and residential buildings'
    },
    {
      title: 'Roofing Projects',
      description: 'Structural timber restoration and full roofing solutions across Cape Town'
    },
    {
      title: 'Maintenance Contracts',
      description: 'Ongoing maintenance programs for property portfolios'
    }
  ];

  const stats = [
    { value: '30+', label: 'Years of Experience' },
    { value: '5', label: 'Mobile Teams' },
    { value: 'R34M+', label: 'Largest Project Value' },
    { value: '100%', label: 'Black-Owned' }
  ];

  useEffect(() => {
    fetchAllProjects();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [activeCategory, activeType]);

  const fetchAllProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const featuredProjects = response.data.filter((p) => p.featured);
      setFeatured(featuredProjects);
    } catch (error) {
      console.error('Error fetching featured projects:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (activeCategory !== 'all') params.append('category', activeCategory);
      if (activeType !== 'all') params.append('project_type', activeType);

      const response = await axios.get(`${API}/projects?${params.toString()}`);
      // Exclude featured from the grid (they get their own section)
      const nonFeatured = response.data.filter((p) => !p.featured);
      setProjects(nonFeatured);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="projects-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/d5be84h6_Ipsa1.jpg"
            alt="Project Highlights"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="overline mb-6 block">Project Highlights</span>
            <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl text-text-main tracking-tight">
              Featured <span className="text-primary">Projects</span>
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-text-muted font-body text-base md:text-lg leading-relaxed">
              Showcasing our expertise in delivering quality construction projects across South
              Africa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* View Toggle: Featured Work | Current Projects */}
      <div className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-2 sm:gap-4 py-4">
            {[
              { id: 'featured', label: 'Featured Work' },
              { id: 'current', label: 'Current Projects' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                data-testid={`view-tab-${tab.id}`}
                onClick={() => setView(tab.id)}
                className={`relative px-4 sm:px-6 py-2 font-mono text-[11px] sm:text-xs uppercase tracking-widest transition-colors ${
                  view === tab.id ? 'text-primary' : 'text-text-muted hover:text-text-main'
                }`}
              >
                {tab.label}
                {view === tab.id && (
                  <motion.span
                    layoutId="projects-view-underline"
                    className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-primary"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'current' && <CurrentProjects />}

      {view === 'featured' && (
      <>
      {/* Featured Projects (Hero Projects) */}
      {featured.length > 0 && (
        <>
          {featured.map((project, index) => (
            <FeaturedProjectBlock key={project.id} project={project} index={index} />
          ))}
        </>
      )}

      {/* Our Expertise */}
      <section
        ref={expertiseRef}
        data-testid="expertise-section"
        className="py-24 md:py-32 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={expertiseInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              Our Expertise
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight"
            >
              Beyond Major <span className="text-primary">Projects</span>
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 max-w-2xl text-text-muted font-body leading-relaxed"
            >
              We deliver daily excellence across multiple construction disciplines.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={expertiseInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {expertiseAreas.map((area) => (
              <motion.div
                key={area.title}
                variants={fadeUpVariants}
                className="bg-surface border border-border p-8"
              >
                <h3 className="font-heading text-xl text-text-main mb-3 tracking-tight">
                  {area.title}
                </h3>
                <p className="text-text-muted font-body text-sm leading-relaxed">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={expertiseInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border border-border bg-surface p-10 md:p-12"
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.label}
                variants={fadeUpVariants}
                className="text-center"
              >
                <div className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-primary">
                  {stat.value}
                </div>
                <div className="font-mono text-xs uppercase tracking-widest text-text-muted mt-3">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid Title + Filters */}
      <section
        ref={gridRef}
        data-testid="portfolio-section"
        className="py-24 md:py-32 bg-surface border-t border-border"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline">Full Portfolio</span>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight">
              More <span className="text-primary">Projects</span>
            </h2>
            <p className="mt-4 max-w-2xl text-text-muted font-body leading-relaxed">
              Browse our broader portfolio of completed renovations and construction work.
            </p>
          </motion.div>

          {/* Filters */}
          <div className="mt-12 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`filter-category-${cat.id}`}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all ${
                    activeCategory === cat.id
                      ? 'bg-primary text-background'
                      : 'border border-border text-text-muted hover:border-primary hover:text-primary'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              {types.map((type) => (
                <button
                  key={type.id}
                  data-testid={`filter-type-${type.id}`}
                  onClick={() => setActiveType(type.id)}
                  className={`px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all ${
                    activeType === type.id
                      ? 'bg-primary text-background'
                      : 'border border-border text-text-muted hover:border-primary hover:text-primary'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="mt-16">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-background animate-pulse aspect-[4/3]" />
                ))}
              </div>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate={gridInView ? 'visible' : 'hidden'}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                <AnimatePresence mode="popLayout">
                  {projects.map((project, index) => (
                    <motion.div
                      key={project.id}
                      variants={fadeUpVariants}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                      layout
                    >
                      <Link
                        to={`/projects/${project.id}`}
                        data-testid={`project-card-${project.id}`}
                        className="project-card group block bg-background border border-border overflow-hidden h-full"
                      >
                        <div className="image-hover-container aspect-[4/3]">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-4 mb-3">
                            <span className="font-mono text-xs uppercase tracking-widest text-primary">
                              {project.category}
                            </span>
                            <span className="w-1 h-1 rounded-full bg-text-muted" />
                            <span className="font-mono text-xs uppercase tracking-widest text-text-muted">
                              {project.type}
                            </span>
                          </div>
                          <h3 className="font-heading text-xl md:text-2xl text-text-main group-hover:text-primary transition-colors">
                            {project.title}
                          </h3>
                          <p className="text-text-muted font-body text-sm mt-3 line-clamp-2">
                            {project.description}
                          </p>
                          <div className="flex items-center gap-4 mt-4 text-text-muted text-xs">
                            <span className="flex items-center gap-1">
                              <MapPin size={12} />
                              {project.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {project.year}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}

            {!loading && projects.length === 0 && (
              <div className="text-center py-16">
                <p className="text-text-muted font-body">
                  No projects found matching your filters.
                </p>
                <button
                  onClick={() => {
                    setActiveCategory('all');
                    setActiveType('all');
                  }}
                  className="mt-4 text-primary font-mono text-xs uppercase tracking-widest hover:underline"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
      </>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-background border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline">Start Your Project</span>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight">
              Ready to Start<br />
              <span className="text-primary">Your Project?</span>
            </h2>
            <p className="mt-6 text-text-muted font-body text-base leading-relaxed max-w-xl mx-auto">
              Let&apos;s discuss how we can bring your construction vision to life with quality and
              professionalism.
            </p>
            <div className="mt-12">
              <Link
                to="/contact"
                data-testid="projects-cta-btn"
                className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2"
              >
                Get in Touch
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
